import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import YouTube from 'react-youtube-embed';
import { Trash2, Edit } from 'lucide-react';
import { Can } from '@new/casl';
import { useDeleteNewlaneTutorialMutation, type NewlaneTutorial } from '@teachur/http-client';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@new/common/ui';

interface TutorialCardProps {
  tutorial: NewlaneTutorial;
}

export const TutorialCard: FC<TutorialCardProps> = ({ tutorial }) => {
  const navigate = useNavigate();
  const deleteMutation = useDeleteNewlaneTutorialMutation({
    onSuccess: () => toast.success('Tutorial deleted successfully!'),
    onError: () => toast.error('Failed to delete tutorial!'),
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this tutorial?')) {
      setIsDeleting(true);
      try {
        await deleteMutation.mutateAsync(tutorial.id);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleEdit = () => {
    navigate({ to: `/newlane-tutorials/${tutorial.id}/edit` });
  };

  return (
    <div className="flex flex-col p-5 items-start gap-4 rounded-[20px] border border-(--neutral-white-NW800) bg-(--neutral-white-NW1000)">
      {tutorial.url && (
        <div className="h-fit self-stretch rounded-[20px] overflow-hidden bg-[#D9D9D9]">
          <YouTube id={tutorial.url} />
        </div>
      )}

      <div className="flex flex-col items-start gap-1 self-stretch">
        <div className="flex justify-between items-start gap-2 w-full">
          <span className="text-(--neutral-black-NB1000) font-[Arvo] text-[20px] font-bold leading-[125%] m-0! line-clamp-2">
            {tutorial.name}
          </span>
        </div>

        {tutorial.description && (
          <p className="text-[#2B383B] font-[Montserrat] text-[14px] font-normal leading-[150%] m-0! line-clamp-3">
            {tutorial.description}
          </p>
        )}

        <Can I="update" a="Course">
          <div className="flex gap-2 w-full shrink-0 self-end">
            <Button
              onClick={handleEdit}
              className="flex-1 rounded-md hover:bg-gray-100 transition-colors"
              title="Edit tutorial"
            >
              <Edit size={18} className="text-(--neutral-white-NW1000)" />
            </Button>
            <Button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex-1 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
              title="Delete tutorial"
            >
              <Trash2 size={18} className="text-(--neutral-white-NW1000)" />
            </Button>
          </div>
        </Can>
      </div>
    </div>
  );
};
